import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Spinner,
  Alert,
} from 'reactstrap';
import { useParams, useHistory } from 'react-router-dom';
import { Form } from '@availity/form';
import { Input, Select } from '../../lib/form-field';
import { useFormikContext } from 'formik';
import '@availity/yup';
import toastr from 'toastr';

import * as landingPagesActions from '../../store/landingPages/actions';
import * as landingTemplatesActions from '../../store/landingTemplates/actions';
import * as campaignsActions from '../../store/campaigns/actions';
import { useDispatch, useSelector } from 'react-redux';
import { actualCountry } from '../../selectors/country';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import Editor from '../../components/LandingPageEditor/Editor';
import LandingComponentInserter from '../../components/LandingPageEditor/LandingComponentInserter';
import formValidation from '../../components/LandingPages/formValidation';
import {
  LANDING_COMPONENT_TYPES,
  createDefaultLandingComponents,
  normalizeLandingComponents,
  transformLandingComponentMarkers,
  validateLandingComponentMarkers,
} from '../../components/LandingPageEditor/landingComponents';
import i18n from '../../components/LandingPageEditor/i18n';
import ComponentSettingsPanel from '../../components/LandingPages/ComponentSettingsPanel';

const PREVIEW_BANNER =
  '<div style="position:fixed;top:0;left:0;right:0;z-index:99999;background:#1a1d1f;color:#fff;text-align:center;font:600 13px sans-serif;padding:8px;">PREVIEW — leads are not saved</div>';

const injectPreviewBanner = (html) =>
  /<body[^>]*>/i.test(html)
    ? html.replace(/<body[^>]*>/i, (match) => match + PREVIEW_BANNER)
    : PREVIEW_BANNER + html;

const openPreviewWindow = (html) => {
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) {
    toastr.error('Allow pop-ups to preview the transformed landing page.', 'Error');
    return;
  }

  previewWindow.document.open();
  previewWindow.document.write(html);
  previewWindow.document.close();
};

const showMarkerValidationErrors = (errors) => {
  const groups = errors.reduce((acc, error) => {
    const title = error.includes(' requires ')
      ? error.split(' requires ')[0]
      : 'Landing components';
    (acc[title] = acc[title] || []).push(error);
    return acc;
  }, {});
  Object.entries(groups).forEach(([title, list]) =>
    toastr.error(list.join(' '), title),
  );
};

const TemplateLoader = ({ landingTemplates, editorRef, isEditMode }) => {
  const { values } = useFormikContext();
  const selectedTemplateId = values.landingTemplateId;

  useEffect(() => {
    if (!isEditMode && selectedTemplateId) {
      const template = landingTemplates.find(
        (t) => t.id === selectedTemplateId,
      );
      if (
        template &&
        template.design &&
        editorRef.current &&
        editorRef.current.editor
      ) {
        editorRef.current.editor.loadDesign(template.design);
      }
    }
  }, [editorRef, isEditMode, landingTemplates, selectedTemplateId]);

  return null;
};

const DependentSelects = ({ countryId, landingTemplates, campaigns }) => {
  const selectedCountryId = countryId;

  const filteredTemplates = Array.isArray(landingTemplates)
    ? landingTemplates
        .filter((t) => !t.archived && t.countryId === selectedCountryId)
        .map(({ id, name }) => ({ value: id, label: name }))
    : [];

  const filteredCampaigns = Array.isArray(campaigns)
    ? campaigns
        .filter((c) => c.countryId === selectedCountryId)
        .map(({ id, name }) => ({ value: id, label: name }))
    : [];

  return (
    <>
      <Select
        name="landingTemplateId"
        id="landingTemplateId"
        label="Landing Template"
        required
        isMulti={false}
        options={filteredTemplates}
      />
      <Select
        name="campaignId"
        id="campaignId"
        label="Campaign"
        required
        isMulti={false}
        options={filteredCampaigns}
      />
    </>
  );
};

const countMarkers = (design) => {
  const json = JSON.stringify(design || {});
  const counts = {};
  Object.values(LANDING_COMPONENT_TYPES).forEach((type) => {
    counts[type] = 0;
  });
  json.split('data-lead1-component=').slice(1).forEach((chunk) => {
    const head = chunk.slice(0, 40);
    Object.values(LANDING_COMPONENT_TYPES).forEach((type) => {
      if (head.indexOf(type) !== -1) counts[type] += 1;
    });
  });
  return counts;
};

const LandingPageEditor = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const history = useHistory();
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [markerCounts, setMarkerCounts] = useState({});

  const handleEditorReady = useCallback(() => {
    const editor = editorRef.current && editorRef.current.editor;
    if (!editor) return;
    const refresh = () =>
      editor.saveDesign((design) => setMarkerCounts(countMarkers(design)));
    refresh();
    // design:loaded covers programmatic loadDesign (template selection and
    // component insertion); design:updated covers manual canvas edits.
    editor.addEventListener('design:loaded', refresh);
    editor.addEventListener('design:updated', () => {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(refresh, 600);
    });
  }, []);

  const loadingPages = useSelector((state) => state.LandingPage.loading);
  const savedPublicUrl = useSelector(
    (state) => state.LandingPage.savedPublicUrl,
  );
  const landingPages = useSelector((state) => state.LandingPage.landingPages);
  const landingTemplates = useSelector(
    (state) => state.LandingTemplate.landingTemplates,
  );
  const campaigns = useSelector((state) => state.Campaign.campaigns);
  const country = useSelector(actualCountry);
  const countryId = country ? country.id : null;

  const loading = loadingPages;

  const page = isEditMode
    ? landingPages.find((p) => p.id === id) || null
    : null;

  const [showUrlAlert, setShowUrlAlert] = useState(false);

  useEffect(() => {
    if (countryId) {
      dispatch(campaignsActions.syncCampaigns());
      dispatch(landingTemplatesActions.syncLandingTemplates());
    }
    if (isEditMode && countryId) {
      dispatch(landingPagesActions.syncLandingPages());
    }

    return () => {
      dispatch(landingPagesActions.resetState());
      dispatch(landingTemplatesActions.resetState());
    };
  }, [dispatch, isEditMode, countryId]);

  useEffect(() => {
    if (savedPublicUrl) {
      setShowUrlAlert(true);
    }
  }, [savedPublicUrl]);

  const handlePublish = (values) => {
    if (editorRef.current && editorRef.current.editor) {
      editorRef.current.editor.exportHtml(({ design, html }) => {
        const landingComponents = normalizeLandingComponents(
          values.landingComponents,
        );
        const errors = validateLandingComponentMarkers({
          html,
          landingComponents,
        });

        if (errors.length) {
          showMarkerValidationErrors(errors);
          return;
        }

        dispatch(
          landingPagesActions.saveLandingPage({
            ...values,
            landingComponents,
            design,
            html,
            id: page ? page.id : undefined,
          }),
        );
      });
    } else {
      dispatch(
        landingPagesActions.saveLandingPage({
          ...values,
          landingComponents: normalizeLandingComponents(
            values.landingComponents,
          ),
          design: page ? page.design : null,
          html: page ? page.html : null,
          id: page ? page.id : undefined,
        }),
      );
    }
  };

  const handlePreview = (values) => {
    if (!editorRef.current || !editorRef.current.editor) return;

    editorRef.current.editor.exportHtml(({ html }) => {
      const landingComponents = normalizeLandingComponents(
        values.landingComponents,
      );
      const errors = validateLandingComponentMarkers({
        html,
        landingComponents,
      });

      if (errors.length) {
        showMarkerValidationErrors(errors);
        return;
      }

      const previewHtml = transformLandingComponentMarkers({
        html,
        landingComponents,
        runtime: {
          campaignId: values.campaignId || 'preview',
          countryId: countryId || 'preview',
          crmEndpoint: 'preview',
        },
      });

      openPreviewWindow(injectPreviewBanner(previewHtml));
    });
  };

  const initialDesign = page ? page.design : null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={isEditMode ? 'Edit Landing Page' : 'New Landing Page'}
            loading={loading}
          />
          {showUrlAlert && savedPublicUrl && (
            <Alert
              color="success"
              toggle={() => setShowUrlAlert(false)}
              className="mb-3">
              <strong>Published!</strong> Your landing page is live at:{' '}
              <a
                href={savedPublicUrl}
                target="_blank"
                rel="noopener noreferrer">
                {savedPublicUrl}
              </a>
            </Alert>
          )}
          <Form
            initialValues={{
              name: page ? page.name || '' : '',
              landingTemplateId: page ? page.landingTemplateId || '' : '',
              campaignId: page ? page.campaignId || '' : '',
              description: page ? page.description || '' : '',
              landingComponents: (() => {
                const normalized = normalizeLandingComponents(
                  page
                    ? page.landingComponents
                    : createDefaultLandingComponents(),
                );
                if (!page && country && country.languageTag) {
                  const language = country.languageTag;
                  const consentDefaults = i18n.getConsentDefaults(language);
                  normalized.shared = {
                    ...normalized.shared,
                    language,
                    privacyNoteText: consentDefaults.privacyNoteText,
                    consents: normalized.shared.consents.map((consent) => ({
                      ...consent,
                      text: consentDefaults.byName[consent.name] || consent.text,
                    })),
                  };
                  const componentDefaults = i18n.getComponentDefaults(language);
                  Object.keys(componentDefaults).forEach((type) => {
                    normalized[type] = {
                      ...normalized[type],
                      ...componentDefaults[type],
                    };
                  });
                }
                return normalized;
              })(),
            }}
            validationSchema={formValidation}
            onSubmit={handlePublish}
            enableReinitialize>
            {({ isValid, values }) => (
              <Row>
                <Col md="3">
                  <Card>
                    <CardBody>
                      <h5 className="font-size-14 mb-3">Page Info</h5>
                      {loading && (
                        <div className="text-center mb-3">
                          <Spinner color="secondary" />
                        </div>
                      )}
                      <Input type="text" name="name" id="name" required />
                      <DependentSelects
                        countryId={countryId}
                        landingTemplates={landingTemplates}
                        campaigns={campaigns}
                      />
                      <Input
                        type="text"
                        name="description"
                        id="description"
                        label="Description"
                      />
                      <LandingComponentInserter editorRef={editorRef} />
                      <ComponentSettingsPanel markerCounts={markerCounts} />
                      <TemplateLoader
                        landingTemplates={landingTemplates}
                        editorRef={editorRef}
                        isEditMode={isEditMode}
                      />
                      <div className="mt-3">
                        <button
                          type="submit"
                          disabled={!isValid || loading}
                          className="btn btn-success waves-effect waves-light w-100">
                          <i className="bx bx-cloud-upload font-size-16 align-middle mr-2"></i>
                          {isEditMode ? 'Update & Publish' : 'Publish'}
                        </button>
                      </div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          color="info"
                          outline
                          className="w-100"
                          disabled={loading}
                          onClick={() => handlePreview(values)}>
                          Preview Components
                        </Button>
                      </div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          className="w-100"
                          onClick={() => history.push('/landing-pages')}>
                          Cancel
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="9">
                  <Card>
                    <CardBody style={{ padding: 0 }}>
                      <Editor
                        editorRef={editorRef}
                        initialDesign={initialDesign}
                        onReady={handleEditorReady}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LandingPageEditor;
